<div class="bg-white border-b border-gray-200 px-6 pt-5 pb-6">
  <div class="flex flex-row justify-between items-center">
    <div>
      <h1 class="font-bold text-2xl text-gray-900 tracking-tight">Create new sale</h1>
      <p class="text-sm text-gray-500 mt-0.5">Step {{ currentStep }} of {{ steps.length }} - {{ steps[currentStep - 1].label }}</p>
    </div>
    <button
      class="h-9 w-9 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-900 flex items-center justify-center transition-colors"
      matTooltip="Cancel this sale"
      (click)="onClose()"
      >
      <mat-icon class="!text-[20px] !h-5 !w-5">close</mat-icon>
    </button>
  </div>

  <!-- Steps: circles joined by a line that fills as you move through the sale -->
  <ol class="mt-6 flex items-start">
    @for (step of steps; track step.id; let last = $last) {
      <li class="flex items-start" [class.flex-1]="!last">
        <button
          type="button"
          class="group flex flex-col items-center text-center w-28 sm:w-36 focus:outline-none"
          [disabled]="step.id >= currentStep || orderSubmitted"
          [class.cursor-default]="step.id >= currentStep"
          (click)="goToStep(step.id)"
          [matTooltip]="step.id < currentStep ? 'Go back to ' + step.label : ''"
          >
          <span
            class="relative h-11 w-11 rounded-full flex items-center justify-center border-2 transition-all duration-300"
          [ngClass]="
            step.id < currentStep
              ? 'bg-emerald-500 border-emerald-500 text-white group-hover:bg-emerald-600'
              : step.id === currentStep
                ? 'bg-neutral-900 border-neutral-900 text-amber-400 ring-4 ring-amber-200 shadow-lg'
                : 'bg-white border-gray-300 text-gray-400'
          "
            >
            @if (step.id < currentStep) {
              <mat-icon class="!text-[22px] !h-[22px] !w-[22px]">check</mat-icon>
            } @else {
              <mat-icon class="!text-[22px] !h-[22px] !w-[22px]">{{ step.icon }}</mat-icon>
            }
          </span>
          <span class="mt-2 text-sm font-semibold" [ngClass]="step.id <= currentStep ? 'text-gray-900' : 'text-gray-400'">{{ step.label }}</span>
          <span class="text-xs mt-0.5 max-w-full truncate px-1" [ngClass]="step.id < currentStep ? 'text-emerald-600 font-medium' : 'text-gray-400'">
            {{ stepSummary(step.id) }}
          </span>
        </button>
        @if (!last) {
          <div class="flex-1 mt-[21px] h-1 mx-1 rounded-full bg-gray-200 overflow-hidden">
            <div class="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500 ease-out"
            [style.width.%]="step.id < currentStep ? 100 : 0"></div>
          </div>
        }
      </li>
    }
  </ol>
</div>

<!-- Started from an abandoned web cart -->
@if (cartLead) {
  <div class="mx-6 mt-4 rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="font-semibold flex items-center gap-1.5"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">remove_shopping_cart</mat-icon> Basket loaded from {{ cartLead.name || cartLead.email || 'a website cart' }}'s abandoned cart, at today's prices</div>
        <div class="text-xs mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-violet-800">
          @if (cartLead.email) { <span>{{ cartLead.email }}</span> }
          @if (cartLead.phone) { <span>{{ cartLead.phone }}</span> }
          @if (cartLead.postCode) { <span>{{ cartLead.postCode }}</span> }
          <span>{{ cartLead.account ? 'Their account is selected below' : 'No account on this email - create one or use guest mode' }}</span>
        </div>
        @if (cartLead.priceChanges.length || cartLead.shortOfStock.length || cartLead.dropped.length) {
          <ul class="mt-2 text-xs list-disc pl-5 space-y-0.5">
            @for (p of cartLead.priceChanges; track $index) { <li>{{ p.name }}: price now £{{ p.now | number:'1.2-2' }} (was £{{ p.was | number:'1.2-2' }} in their cart)</li> }
            @for (p of cartLead.shortOfStock; track $index) { <li class="text-red-700">{{ p.name }}: they wanted {{ p.wanted }}, only {{ p.available }} in stock - adjust before placing the order</li> }
            @for (n of cartLead.dropped; track $index) { <li class="text-red-700">{{ n }}: no longer for sale, left out</li> }
          </ul>
        }
      </div>
      <button class="h-7 w-7 rounded-full hover:bg-violet-100 flex items-center justify-center flex-shrink-0" (click)="cartLead = null" aria-label="Dismiss"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon></button>
    </div>
  </div>
}

<!-- progress bar -->
@if(progress) {
  <div class="mt-4 mx-5 bg-neutral-900 rounded-md">
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
  </div>
}
<!-- progress bar -->

<div class="bg-gray-50 min-h-[calc(100vh-200px)] px-6 py-6 pb-10">
  <div class="grid grid-cols-1 gap-6 items-start max-w-[1700px] mx-auto"
    [ngClass]="localCart.items?.length > 0 ? 'xl:grid-cols-5' : 'xl:grid-cols-1'">

    <!-- Content area -->
    <div class="min-w-0" [ngClass]="localCart.items?.length > 0 ? 'xl:col-span-3' : ''">
      <!-- Reusable: one purchasable variant (or slab / crate) -->
      <ng-template #variantCard let-v="v" let-slab="slab">
        <div
          class="rounded-xl border bg-white p-4 flex flex-col md:flex-row md:items-center gap-4 transition-all"
          [ngClass]="v.stockQty <= 0 ? 'border-gray-200 opacity-60' : 'border-gray-200 hover:border-amber-300 hover:shadow-md'"
          >
          <div class="flex-1 min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-base font-bold text-gray-900">{{ v.format || v.sku }}</span>
              @if (v?.deliveryterm?.deliveryTermDesc) {
                <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{{ v.deliveryterm.deliveryTermDesc }}</span>
              }
              @if (v.discPrice > 0) {
                <span class="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Save {{ savingPercent(v) }}%</span>
              }
            </div>

            <!-- Facts an agent can quote to the customer -->
            <div class="mt-3 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2">
              @if (v.approxCoverage > 0) {
                <div class="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                  <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Covers</div>
                  <div class="text-sm font-bold text-gray-900">{{ v.approxCoverage | number:'1.0-2' }} m²</div>
                </div>
              }
              @if (v.approxTiles > 0) {
                <div class="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                  <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Pieces</div>
                  <div class="text-sm font-bold text-gray-900">{{ v.approxTiles }}</div>
                </div>
              }
              @if (pricePerM2(v) > 0) {
                <div class="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
                  <div class="text-[10px] font-semibold text-amber-700/70 uppercase tracking-wide">Per m²</div>
                  <div class="text-sm font-bold text-amber-900">£{{ pricePerM2(v) | number:'1.2-2' }}</div>
                </div>
              }
              <div class="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Weight</div>
                <div class="text-sm font-bold text-gray-900">{{ v.weight | number:'1.0-2' }} kg</div>
              </div>
              @if (weightPerM2(v) > 0) {
                <div class="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                  <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Per m²</div>
                  <div class="text-sm font-bold text-gray-900">{{ weightPerM2(v) | number:'1.0-1' }} kg</div>
                </div>
              }
              @if (dimensionsOf(v); as dims) {
                <div class="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                  <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Size (mm)</div>
                  <div class="text-sm font-bold text-gray-900">{{ dims }}</div>
                </div>
              }
            </div>

            <div class="mt-2.5 flex flex-wrap items-center gap-1.5">
              @if (v.stockQty <= 0) {
                <span class="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                  <mat-icon class="!text-[13px] !h-[13px] !w-[13px]">block</mat-icon> Out of stock
                </span>
              } @else {
                <span class="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                  <mat-icon class="!text-[13px] !h-[13px] !w-[13px]">warehouse</mat-icon> Markstey {{ v.mStockQty }}
                </span>
                <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Total stock {{ v.stockQty }}</span>
                @if (v.stockQty <= 5) {
                  <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700">Low stock</span>
                }
              }
              <button type="button" class="flex items-center gap-1 text-[11px] font-mono text-gray-400 hover:text-gray-800 px-1.5 py-0.5 rounded transition-colors" (click)="copyText(v.sku, 'SKU')" matTooltip="Copy SKU">
                {{ v.sku }} <mat-icon class="!text-[12px] !h-3 !w-3">content_copy</mat-icon>
              </button>
            </div>
          </div>

          <div class="md:w-32 md:text-right">
            @if (v.discPrice > 0) {
              <div class="text-xs text-gray-400 line-through">£{{ v.price }}</div>
              <div class="text-xl font-extrabold text-emerald-700 leading-tight">£{{ v.discPrice }}</div>
            } @else {
              <div class="text-xl font-extrabold text-gray-900 leading-tight">£{{ v.price }}</div>
            }
          </div>

          <div class="flex items-center gap-3">
            <div class="flex items-center rounded-xl border border-gray-200 overflow-hidden bg-white" [ngClass]="{ 'opacity-40 pointer-events-none': v.stockQty <= 0 }">
              <button type="button" class="h-10 w-9 flex items-center justify-center text-gray-400 hover:bg-gray-50 hover:text-gray-800"
                (click)="v.quantity = (v.quantity || 0) > 0 ? v.quantity - 1 : 0; slab && onQuantityChange()">
                <mat-icon class="!text-[18px]">remove</mat-icon>
              </button>
              <input type="number" min="0"
                class="w-14 text-center text-sm font-semibold outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                [ngModel]="v.quantity" (ngModelChange)="v.quantity = $event; slab && onQuantityChange()" [disabled]="v.stockQty <= 0" />
                <button type="button" class="h-10 w-9 flex items-center justify-center text-gray-400 hover:bg-gray-50 hover:text-gray-800"
                  (click)="v.quantity = (v.quantity || 0) + 1; slab && onQuantityChange()">
                <mat-icon class="!text-[18px]">add</mat-icon>
              </button>
            </div>
            <button type="button"
              class="flex items-center gap-1.5 px-4 h-10 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-semibold text-sm shadow-sm hover:from-amber-300 hover:to-amber-400 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              (click)="onAddToCart(v)" [disabled]="v.stockQty <= 0">
              <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">add_shopping_cart</mat-icon> Add
            </button>
          </div>
        </div>
      </ng-template>

      <!-- Reusable: discount codes an agent can offer -->
      <ng-template #couponCards let-list="list" let-cols="cols">
        <div class="grid gap-3" [ngClass]="cols === 1 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'">
          @for (c of list; track c.code) {
            <div class="flex rounded-xl border bg-white overflow-hidden transition-all"
              [ngClass]="c.status === 'applies' ? 'border-emerald-200 hover:shadow-md' : c.status === 'needsSpend' ? 'border-amber-200' : 'border-gray-200 opacity-90'">
              <!-- The offer -->
              <div class="w-24 flex-shrink-0 flex flex-col items-center justify-center px-2 py-3 text-center border-r border-dashed"
                [ngClass]="c.status === 'applies' ? 'bg-emerald-50 border-emerald-200' : c.status === 'needsSpend' ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'">
                <div class="text-2xl font-extrabold leading-none" [ngClass]="c.status === 'applies' ? 'text-emerald-700' : c.status === 'needsSpend' ? 'text-amber-700' : 'text-gray-500'">
                  {{ c.percent ? (c.value | number:'1.0-1') + '%' : '£' + (c.value | number:'1.0-2') }}
                </div>
                <div class="text-[10px] font-bold uppercase tracking-widest text-gray-400 mt-1">off</div>
              </div>

              <div class="flex-1 min-w-0 p-3">
                <div class="flex items-center gap-1.5">
                  <span class="font-mono text-sm font-bold text-gray-900 truncate">{{ c.code }}</span>
                  <button type="button" class="h-6 w-6 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center flex-shrink-0" (click)="copyText(c.code, 'Code')" matTooltip="Copy the code">
                    <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">content_copy</mat-icon>
                  </button>
                </div>
                @if (c.description) {
                  <div class="text-xs text-gray-600 leading-snug truncate" [matTooltip]="c.description">{{ c.description }}</div>
                }

                <!-- What using it means for the order -->
                @if (c.status === 'needsSpend') {
                  <div class="mt-2">
                    <div class="h-1.5 rounded-full bg-amber-100 overflow-hidden"><div class="h-full rounded-full bg-gradient-to-r from-amber-400 to-emerald-500" [style.width.%]="couponProgress(c)"></div></div>
                    <div class="mt-1.5 text-xs font-semibold text-amber-800 leading-snug">
                      Add £{{ c.shortfall | number:'1.2-2' }} more: the customer saves about £{{ c.saving | number:'1.2-2' }} and the order grows by £{{ c.shortfall | number:'1.2-2' }}
                    </div>
                  </div>
                } @else {
                  <div class="mt-2 text-xs font-semibold text-rose-700 flex items-center gap-1 leading-snug">
                    <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">trending_down</mat-icon>
                    Takes £{{ c.saving | number:'1.2-2' }} off this order ({{ couponCostPercent(c) | number:'1.0-1' }}%)
                  </div>
                }

                <div class="mt-2 flex flex-wrap gap-1">
                  @if (c.minSpend) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Min spend £{{ c.minSpend }}</span> }
                  @if (c.maxSpend) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Max spend £{{ c.maxSpend }}</span> }
                  @if (c.oncePerCustomer) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Once per customer</span> }
                  @if (c.alone) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Can't be combined</span> }
                  @if (c.excludesSale) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Excludes sale items</span> }
                  @if (c.restrictedToProducts || c.restrictedToCategories) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{{ c.restrictedToProducts ? 'Selected products' : 'Selected categories' }} only</span> }
                  @if (c.freeGift) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">Free gift</span> }
                  @if (c.staffOnly) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-neutral-900 text-amber-300">Staff only</span> }
                  @if (c.customerSpecific) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">For this customer</span> }
                  @if (c.recentUses > 0) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Used {{ c.recentUses }}× {{ c.windowDays === 30 ? 'this month' : c.windowDays === 90 ? 'in 3 months' : 'in the last year' }}</span> }
                  @if (c.usesLeft !== null) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-50 text-rose-700">{{ c.usesLeft }} use{{ c.usesLeft === 1 ? '' : 's' }} left</span> }
                  @if (c.expires) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-50 text-rose-700">Ends {{ c.expires | date:'d MMM yyyy' }}</span> }
                </div>
              </div>

              <div class="flex items-center pr-3">
                @if (isCouponApplied(c.code)) {
                  <span class="flex items-center gap-1 text-xs font-bold text-emerald-700"><mat-icon class="!text-[16px] !h-4 !w-4">check</mat-icon> Applied</span>
                } @else if (c.status === 'needsSpend') {
                  <button type="button" class="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold bg-amber-100 text-amber-900 hover:bg-amber-200 transition-colors"
                    (click)="copyText(couponPitch(c), 'Pitch')" matTooltip="Copy a line to say to the customer">
                    <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">chat</mat-icon> Copy pitch
                  </button>
                } @else {
                  <button type="button" class="px-3 py-2 rounded-lg text-xs font-bold bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                  (click)="applySuggestedCoupon(c)" matTooltip="Use only to close a hesitant customer">Use to close</button>
                }
              </div>
            </div>
          }
        </div>
      </ng-template>

      <!-- Reusable: discount guidance - lead with codes that grow the order, hold the rest back -->
      <ng-template #couponGuide let-cols="cols">
        <div class="rounded-xl bg-amber-50/70 border border-amber-100 px-4 py-3 text-xs text-amber-900 flex items-start gap-2 mb-4">
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-amber-500 mt-0.5">lightbulb</mat-icon>
          <span>Every code costs margin. <strong>Lead with codes that lift the order</strong>, and keep the ones that already apply in reserve for a customer who is hesitating.</span>
        </div>

        @if (couponBoosters.length) {
          <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Grow the order</div>
          <ng-container *ngTemplateOutlet="couponCards; context: { list: couponBoosters, cols: cols }"></ng-container>
        } @else {
          <div class="rounded-xl border border-dashed border-gray-200 px-4 py-3 text-sm text-gray-500">
            No code would grow this order right now. Hold discounts back unless the customer hesitates.
          </div>
        }

        @if (couponReserve.length) {
          <details class="group mt-4 rounded-xl border border-gray-200 bg-white">
            <summary class="cursor-pointer list-none flex items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-gray-800">
              <span class="flex items-center gap-2">
                <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-rose-500">savings</mat-icon>
                Keep in reserve: {{ couponReserve.length }} code{{ couponReserve.length === 1 ? '' : 's' }} that apply now
                <span class="text-xs font-normal text-gray-400">use only to close the sale</span>
              </span>
              <mat-icon class="!text-[20px] !h-5 !w-5 text-gray-400 transition-transform group-open:rotate-180">expand_more</mat-icon>
            </summary>
            <div class="px-4 pb-4">
              <ng-container *ngTemplateOutlet="couponCards; context: { list: couponReserve, cols: cols }"></ng-container>
            </div>
          </details>
        }
      </ng-template>

      <!-- Reusable: what a customer has bought before -->
      <ng-template #suggestionCards let-items="items">
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          @for (r of items; track r.productId) {
            <button type="button" class="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left hover:border-amber-300 hover:shadow-md transition-all" (click)="openSuggestion(r.productSlug)">
              <span class="h-16 w-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                @if (r.image) {
                  <img [src]="r.image" alt="" class="h-full w-full object-cover" />
                } @else {
                  <mat-icon class="!text-gray-300">image</mat-icon>
                }
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-semibold text-gray-900 leading-snug line-clamp-2">{{ r.productName }}</span>
                <span class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                  @if (r.from > 0) {
                    <span class="text-sm font-bold text-gray-900">from £{{ r.from | number:'1.2-2' }}</span>
                  }
                  <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full" [ngClass]="r.inStock ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'">{{ r.inStock ? 'In stock' : 'Out of stock' }}</span>
                </span>
                <span class="mt-1 block text-[11px] text-gray-500">
                  Ordered {{ r.timesOrdered }} time{{ r.timesOrdered === 1 ? '' : 's' }} · {{ r.totalQty }} in total · last {{ r.lastOrdered | date:'MMM yyyy' }}
                </span>
              </span>
              <mat-icon class="!text-gray-300">chevron_right</mat-icon>
            </button>
          }
        </div>
      </ng-template>

      @if (currentStep === 1) {
        <div class="flex flex-col gap-6">

          <!-- Product search -->
          <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div class="flex items-center gap-2 mb-3">
              <span class="h-9 w-9 rounded-xl bg-amber-50 flex items-center justify-center">
                <mat-icon class="!text-amber-500 !text-[20px] !h-5 !w-5">search</mat-icon>
              </span>
              <div>
                <div class="font-semibold text-gray-900">Find a product</div>
                <div class="text-xs text-gray-500">Search by name or SKU, then choose a format and quantity</div>
              </div>
            </div>
            <app-typehead
              [dataSet]="productSuggestionList"
              [keysToMatch]="['name','sku']"
              (onModelChangedEvent)="onProductSelection($event)"
              [resetEachSelect]="true"
            ></app-typehead>
          </div>

          @if (editingProduct) {
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <!-- Product hero: what an agent needs to describe it -->
              <div class="grid grid-cols-1 lg:grid-cols-5 gap-0 border-b border-gray-100">
                <!-- Gallery -->
                <div class="lg:col-span-2 p-5 bg-gray-50/60">
                  <div class="aspect-[4/3] w-full rounded-xl overflow-hidden bg-white border border-gray-100 flex items-center justify-center">
                    @if (mainImage) {
                      <img [src]="mainImage" alt="" class="h-full w-full object-cover" />
                    } @else {
                      <mat-icon class="!text-gray-300 !text-[48px] !h-12 !w-12">image</mat-icon>
                    }
                  </div>
                  @if (productImages.length > 1) {
                    <div class="mt-3 flex gap-2 overflow-x-auto pb-1">
                      @for (img of productImages; track img) {
                        <button type="button" class="h-14 w-14 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all"
                          [ngClass]="img === mainImage ? 'border-amber-400' : 'border-transparent opacity-70 hover:opacity-100'"
                          (click)="galleryImage = img">
                          <img [src]="img" alt="" class="h-full w-full object-cover" />
                        </button>
                      }
                    </div>
                  }
                </div>

                <!-- Facts -->
                <div class="lg:col-span-3 p-5">
                  <div class="flex flex-wrap items-center gap-1.5 mb-2">
                    @for (label of productLabels; track label) {
                      <span class="text-[11px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full bg-red-500 text-white">{{ label }}</span>
                    }
                    @for (cat of productCategoryNames; track cat) {
                      <span class="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{{ cat }}</span>
                    }
                  </div>
                  <div class="flex items-start justify-between gap-3">
                    <h2 class="font-bold text-gray-900 text-xl leading-snug">{{ editingProduct.product.productName }}</h2>
                    <a class="flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 whitespace-nowrap mt-1"
                      href="{{ frontEndURL + editingProduct.product.productSlug }}" target="_blank" rel="noopener">
                      View on site <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">open_in_new</mat-icon>
                    </a>
                  </div>

                  <!-- Key numbers -->
                  <div class="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div class="rounded-xl bg-neutral-900 text-white px-4 py-3">
                      <div class="text-[10px] font-semibold text-amber-400 uppercase tracking-widest">From</div>
                      <div class="text-xl font-extrabold">£{{ fromPrice | number:'1.2-2' }}</div>
                    </div>
                    @if (fromPricePerM2 > 0) {
                      <div class="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
                        <div class="text-[10px] font-semibold text-amber-700/70 uppercase tracking-widest">Best price per m²</div>
                        <div class="text-xl font-extrabold text-amber-900">£{{ fromPricePerM2 | number:'1.2-2' }}</div>
                      </div>
                    }
                    <div class="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                      <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Options</div>
                      <div class="text-xl font-extrabold text-gray-900">{{ editingProduct.variants.length }}</div>
                    </div>
                    <div class="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                      <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">In stock</div>
                      <div class="text-xl font-extrabold" [ngClass]="inStockOptions ? 'text-emerald-700' : 'text-red-600'">{{ inStockOptions }} / {{ editingProduct.variants.length }}</div>
                    </div>
                  </div>

                  <!-- Specifications -->
                  @if (productSpecs.length) {
                    <div class="mt-5">
                      <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Specification</div>
                      <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                        @for (spec of productSpecs; track spec.name + spec.value) {
                          <div class="flex justify-between gap-3 border-b border-gray-50 py-1">
                            <dt class="text-gray-500">{{ spec.name }}</dt>
                            <dd class="font-semibold text-gray-900 text-right">{{ spec.value }}</dd>
                          </div>
                        }
                      </dl>
                    </div>
                  }
                </div>
              </div>

              <!-- Key information: the selling points from the product page -->
              <div class="px-5 py-4 border-b border-gray-100 bg-white">
                <!-- Quick offers -->
                <div class="flex flex-wrap items-center gap-2">
                  @if (productRating > 0) {
                    <span class="flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-100">
                      <mat-icon class="!text-[15px] !h-[15px] !w-[15px] !text-amber-500">star</mat-icon> {{ productRating | number:'1.1-1' }} customer rating
                    </span>
                  }
                  @if (freeSampleSlug) {
                    <a class="flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100 hover:bg-emerald-100 transition-colors"
                      [href]="frontEndURL + freeSampleSlug" target="_blank" rel="noopener" matTooltip="Offer the customer a free sample">
                      <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">redeem</mat-icon> Free sample available
                      <mat-icon class="!text-[13px] !h-[13px] !w-[13px] opacity-60">open_in_new</mat-icon>
                    </a>
                  }
                  @for (d of deliveryPromises; track d) {
                    <span class="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-50 text-sky-800 border border-sky-100">
                      <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">local_shipping</mat-icon> {{ d }}
                    </span>
                  }
                  @if (productInfo?.layingGuide; as guide) {
                    <a class="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors" [href]="guide.href" target="_blank" rel="noopener">
                      <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">picture_as_pdf</mat-icon> {{ guide.text }}
                    </a>
                  }
                </div>

                @if (infoTabs.length) {
                  <div class="mt-4 flex flex-wrap gap-1 border-b border-gray-100">
                    @for (t of infoTabs; track t.key) {
                      <button type="button" class="flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors"
                        [ngClass]="infoTab === t.key ? 'border-amber-400 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'"
                        (click)="infoTab = t.key">
                        <mat-icon class="!text-[17px] !h-[17px] !w-[17px]" [ngClass]="infoTab === t.key ? '!text-amber-500' : ''">{{ t.icon }}</mat-icon>
                        {{ t.label }}
                        <span class="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{{ t.count }}</span>
                      </button>
                    }
                  </div>

                  <div class="pt-4">
                    <!-- Highlights -->
                    @if (infoTab === 'highlights') {
                      <ul class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                        @for (h of productInfo!.highlights; track h) {
                          <li class="flex items-center gap-2.5 rounded-xl bg-emerald-50/60 border border-emerald-100 px-3.5 py-2.5 text-sm font-semibold text-emerald-900">
                            <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-emerald-500">check_circle</mat-icon> {{ h }}
                          </li>
                        }
                      </ul>
                    }

                    <!-- What is in each kit -->
                    @if (infoTab === 'kit') {
                      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        @for (k of productInfo!.kits; track $index) {
                          <div class="rounded-xl border border-gray-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-neutral-900 text-amber-400 text-sm font-bold">{{ k.title || 'Kit contents' }}</div>
                            <table class="w-full text-sm">
                              <thead>
                                <tr class="text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50">
                                  @for (h of k.headers; track $index) {
                                    <th class="px-4 py-2" [class.text-right]="$index > 0">{{ h }}</th>
                                  }
                                </tr>
                              </thead>
                              <tbody>
                                @for (r of k.rows; track $index; let last = $last) {
                                  <tr class="border-t border-gray-50" [class.bg-amber-50]="last && k.rows.length > 1" [class.font-bold]="last && k.rows.length > 1">
                                    @for (c of r; track $index) {
                                      <td class="px-4 py-2 text-gray-800" [class.text-right]="$index > 0">{{ c }}</td>
                                    }
                                  </tr>
                                }
                              </tbody>
                            </table>
                          </div>
                        }
                      </div>
                    }

                    <!-- Good to know -->
                    @if (infoTab === 'notes') {
                      <div class="flex flex-col gap-2">
                        @for (n of productInfo!.notes; track $index) {
                          <details class="group rounded-xl border border-gray-200 bg-white open:border-amber-300 open:bg-amber-50/30 transition-colors">
                            <summary class="cursor-pointer list-none flex items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-gray-900">
                              {{ n.title }}
                              <mat-icon class="!text-[20px] !h-5 !w-5 text-gray-400 transition-transform group-open:rotate-180">expand_more</mat-icon>
                            </summary>
                            <div class="px-4 pb-4 text-sm text-gray-700 leading-relaxed [&_p]:mb-2 [&_a]:underline [&_a]:text-amber-700" [innerHTML]="n.html"></div>
                          </details>
                        }
                      </div>
                    }

                    <!-- Accessories -->
                    @if (infoTab === 'accessories') {
                      <div class="text-xs text-gray-500 mb-3">Customers laying this paving usually also need these. Click one to look at it and add it to the sale.</div>
                      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                        @for (a of productAccessories; track a.slug) {
                          <button type="button" class="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left hover:border-amber-300 hover:shadow-md transition-all" (click)="openAccessory(a.slug)">
                            <span class="h-16 w-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                              @if (a.image) {
                                <img [src]="a.image" alt="" class="h-full w-full object-cover" />
                              } @else {
                                <mat-icon class="!text-gray-300">construction</mat-icon>
                              }
                            </span>
                            <span class="min-w-0 flex-1">
                              <span class="block text-sm font-semibold text-gray-900 leading-snug line-clamp-2">{{ a.name }}</span>
                              <span class="mt-1 flex items-center gap-2">
                                @if (a.from > 0) {
                                  <span class="text-sm font-bold text-gray-900">from £{{ a.from | number:'1.2-2' }}</span>
                                }
                                <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full" [ngClass]="a.inStock ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'">{{ a.inStock ? 'In stock' : 'Out of stock' }}</span>
                              </span>
                            </span>
                            <mat-icon class="!text-gray-300">chevron_right</mat-icon>
                          </button>
                        }
                      </div>
                    }
                  </div>
                }
              </div>

              <div class="p-5 flex flex-col gap-3">
                <!-- Coverage calculator: how many of which format to cover an area -->
                @if (hasCoverageVariants) {
                  <div class="rounded-2xl overflow-hidden border border-neutral-800">
                    <div class="bg-neutral-900 text-white p-4 flex flex-col lg:flex-row lg:items-center gap-4">
                      <div class="flex items-center gap-3 flex-1">
                        <span class="h-10 w-10 rounded-xl bg-amber-400 text-neutral-900 flex items-center justify-center flex-shrink-0">
                          <mat-icon>calculate</mat-icon>
                        </span>
                        <div>
                          <div class="font-semibold">Coverage calculator</div>
                          <div class="text-xs text-white/60">Enter the area to cover. We pick the formats from what is in stock.</div>
                        </div>
                      </div>
                      <div class="flex flex-wrap items-center gap-3">
                        <div class="relative w-40">
                          <input type="number" min="0" step="0.1" placeholder="Area"
                            class="w-full pl-3 pr-10 py-2.5 rounded-lg bg-white text-neutral-900 font-bold outline-none focus:ring-2 focus:ring-amber-400"
                            [(ngModel)]="calcArea" (ngModelChange)="onCoverageInputChange()" />
                          <span class="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-semibold">m²</span>
                        </div>
                        <div class="flex items-center gap-1 rounded-lg bg-white/10 p-1" matTooltip="Extra allowed for cuts and breakage">
                          @for (w of wastageOptions; track w) {
                            <button type="button" class="px-3 py-1.5 rounded-md text-xs font-semibold transition-colors"
                              [ngClass]="calcWastage === w ? 'bg-amber-400 text-neutral-900' : 'text-white/70 hover:bg-white/10'"
                            (click)="setWastage(w)">{{ w === 0 ? 'No extra' : '+' + w + '%' }}</button>
                          }
                        </div>
                        @if (calcArea) {
                          <button type="button" class="h-8 w-8 rounded-full text-white/60 hover:bg-white/10 hover:text-white flex items-center justify-center" (click)="clearCoverage()" aria-label="Clear">
                            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
                          </button>
                        }
                      </div>
                    </div>

                    @if (coverageMessage) {
                      <div class="px-4 py-3 text-sm font-medium bg-amber-50 text-amber-900 border-t border-amber-100 flex items-start gap-2">
                        <mat-icon class="!text-[18px] !h-[18px] !w-[18px] mt-0.5">info</mat-icon> {{ coverageMessage }}
                      </div>
                    }

                    @if (coverageSuggestions.length) {
                      <div class="bg-gray-50 p-4 border-t border-gray-100">
                        <div class="text-xs text-gray-500 mb-3">
                          Covering <strong class="text-gray-800">{{ coverageTarget | number:'1.0-2' }} m²</strong>
                          @if (calcWastage) { ({{ calcArea }} m² + {{ calcWastage }}% extra) }
                          </div>
                          <div class="grid grid-cols-1 xl:grid-cols-3 gap-3">
                            @for (s of coverageSuggestions; track s.key) {
                              <div class="rounded-xl bg-white border p-4 flex flex-col" [ngClass]="s.short ? 'border-red-200' : 'border-gray-200 hover:border-amber-300 hover:shadow-md transition-all'">
                                <div class="flex items-center gap-2 text-sm font-bold text-gray-900">
                                  <mat-icon class="!text-amber-500 !text-[20px] !h-5 !w-5">{{ s.icon }}</mat-icon> {{ s.label }}
                                </div>
                                <ul class="mt-3 flex flex-col gap-1.5">
                                  @for (l of s.lines; track l.variant.productVariantId) {
                                    <li class="flex items-center justify-between gap-2 text-sm">
                                      <span class="text-gray-700"><strong class="text-gray-900">{{ l.qty }} ×</strong> {{ l.variant.format || l.variant.sku }}</span>
                                      <span class="text-gray-500">£{{ l.qty * effectivePrice(l.variant) | number:'1.2-2' }}</span>
                                    </li>
                                  }
                                </ul>
                                <div class="mt-3 pt-3 border-t border-gray-100 grid grid-cols-3 gap-2 text-center">
                                  <div>
                                    <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Covers</div>
                                    <div class="text-sm font-bold text-gray-900">{{ s.coverage | number:'1.0-2' }} m²</div>
                                  </div>
                                  <div>
                                    <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Extra</div>
                                    <div class="text-sm font-bold" [ngClass]="s.extra > 0.005 ? 'text-gray-900' : 'text-emerald-700'">{{ s.extra | number:'1.0-2' }} m²</div>
                                  </div>
                                  <div>
                                    <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Per m²</div>
                                    <div class="text-sm font-bold text-amber-800">£{{ s.perM2 | number:'1.2-2' }}</div>
                                  </div>
                                </div>
                                <div class="mt-3 flex items-end justify-between gap-2">
                                  <div>
                                    <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Total</div>
                                    <div class="text-xl font-extrabold text-gray-900 leading-none">£{{ s.price | number:'1.2-2' }}</div>
                                  </div>
                                  <div class="flex gap-1.5">
                                    <button type="button" class="px-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50" (click)="applyCoverage(s, false)" matTooltip="Fill in the quantities below without adding">Fill in</button>
                                    <button type="button" class="flex items-center gap-1 px-3 py-2 rounded-lg bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 text-xs font-bold hover:from-amber-300 hover:to-amber-400" (click)="applyCoverage(s, true)">
                                      <mat-icon class="!text-[16px] !h-4 !w-4">add_shopping_cart</mat-icon> Add all
                                    </button>
                                  </div>
                                </div>
                              </div>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  }

                  <!-- Template 1: formats -->
                  @if (editingProduct.product.productTemplateId === 1) {
                    @for (variant of editingProduct.variants; track $index) {
                      <ng-container *ngTemplateOutlet="variantCard; context: { v: variant, slab: false }"></ng-container>
                    }
                  }

                  <!-- Template 2: slabs and crates -->
                  @if (editingProduct.product.productTemplateId === 2 && slabsCrates) {
                    <ng-container *ngTemplateOutlet="variantCard; context: { v: slabsCrates.slab, slab: true }"></ng-container>
                    <ng-container *ngTemplateOutlet="variantCard; context: { v: slabsCrates.crate, slab: true }"></ng-container>
                  }
                </div>
              </div>

              @if (editingProduct.product.productDescription) {
                <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <div class="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">About this product</div>
                  <div class="prose prose-sm max-w-none" [innerHTML]="editingProduct.product.productDescription"></div>
                </div>
              }

              @if (editingProduct.faqs && editingProduct.faqs.length > 0) {
                <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <div class="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Frequently asked questions</div>
                  @for (faq of editingProduct.faqs; track $index) {
                    <div class="my-2">
                      <mat-accordion>
                        <mat-expansion-panel (opened)="panelOpenState.set(true)" (closed)="panelOpenState.set(false)">
                          <mat-expansion-panel-header>
                            <mat-panel-title> {{faq.faq.faqQuestion}} </mat-panel-title>
                          </mat-expansion-panel-header>
                          <div [innerHTML]="faq.faq.faqAnswer"></div>
                        </mat-expansion-panel>
                      </mat-accordion>
                    </div>
                  }
                </div>
              }
            } @else if (!localCart.items?.length) {
              <!-- Nothing chosen yet -->
              <div class="rounded-2xl border border-dashed border-gray-300 bg-white/60 px-6 py-14 flex flex-col items-center text-center">
                <span class="h-14 w-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-3">
                  <mat-icon class="!text-amber-500 !text-[30px] !h-[30px] !w-[30px]">inventory_2</mat-icon>
                </span>
                <div class="font-semibold text-gray-800 text-lg">Start by choosing a product</div>
                <div class="text-sm text-gray-500 mt-1 max-w-sm">Search above. Everything you add appears in the basket on the right, with the running total.</div>
              </div>
            }

            <!-- Suggestions from past orders: one shelf per angle -->
            @if (recommendationsLoading && !recommendations) {
              <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div class="flex items-center gap-2 text-sm text-gray-500"><mat-icon class="!text-amber-500 animate-pulse">recommend</mat-icon> Finding suggestions from past orders...</div>
                <div class="mt-4 grid gap-3" [ngClass]="laneGridClass">
                  @for (n of [1,2,3,4,5,6]; track n) {
                    <div class="rounded-xl border border-gray-100 p-3 animate-pulse">
                      <div class="aspect-[4/3] rounded-lg bg-gray-100"></div>
                      <div class="mt-3 h-3 rounded bg-gray-100"></div>
                      <div class="mt-2 h-3 w-2/3 rounded bg-gray-100"></div>
                    </div>
                  }
                </div>
              </div>
            }
            @for (lane of recommendations?.lanes ?? []; track lane.key) {
              <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div class="flex items-start justify-between gap-3">
                  <div class="flex items-center gap-2.5">
                    <span class="h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0" [ngClass]="laneTone[lane.tone].icon"><mat-icon class="!text-[20px] !h-5 !w-5">{{ lane.icon }}</mat-icon></span>
                    <div>
                      <div class="font-semibold text-gray-900 leading-tight">{{ lane.title }}</div>
                      <div class="text-xs text-gray-500">{{ lane.subtitle }}</div>
                    </div>
                  </div>
                  <span class="hidden sm:inline-flex flex-shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-500">Top {{ lane.items.length }}</span>
                </div>

                <div class="mt-4 grid gap-3" [ngClass]="laneGridClass">
                  @for (r of lane.items; track r.productId) {
                    <button type="button" class="group flex flex-col rounded-xl border border-gray-200 bg-white text-left overflow-hidden hover:border-amber-300 hover:shadow-md transition-all" (click)="openSuggestion(r.productSlug)">
                      <span class="relative block aspect-[4/3] bg-gray-100 overflow-hidden">
                        @if (r.image) {
                          <img [src]="r.image" alt="" class="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                        } @else {
                          <span class="h-full w-full flex items-center justify-center"><mat-icon class="!text-gray-300">image</mat-icon></span>
                        }
                        @if (lane.key === 'offers' && r.saving) {
                          <span class="absolute top-2 left-2 text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-500 text-white shadow">-{{ r.saving }}%</span>
                        } @else if (r.label) {
                          <span class="absolute top-2 left-2 max-w-[85%] truncate text-[10px] font-bold px-2 py-0.5 rounded-full bg-neutral-900/85 text-amber-300">{{ r.label }}</span>
                        }
                      </span>
                      <span class="flex flex-col flex-1 p-3">
                        <span class="block text-sm font-semibold text-gray-900 leading-snug line-clamp-2 min-h-[2.5rem]">{{ r.productName }}</span>
                        <span class="mt-auto pt-2 block text-sm font-bold text-gray-900">from £{{ r.from | number:'1.2-2' }}</span>
                        <span class="mt-0.5 block text-[11px] font-semibold leading-snug" [ngClass]="laneTone[lane.tone].note">{{ r.note }}</span>
                      </span>
                    </button>
                  }
                </div>
              </div>
            }

            <!-- Discount codes: only where they help the sale -->
            @if (hasCouponAdvice) {
              <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div class="flex items-center gap-2.5 mb-4">
                  <span class="h-9 w-9 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center flex-shrink-0"><mat-icon class="!text-[20px] !h-5 !w-5">local_offer</mat-icon></span>
                  <div>
                    <div class="font-semibold text-gray-900 leading-tight">Grow the order with a code</div>
                    <div class="text-xs text-gray-500">Live codes customers have used lately, and what each means for this order.</div>
                  </div>
                </div>
                <ng-container *ngTemplateOutlet="couponGuide; context: { cols: 2 }"></ng-container>
              </div>
            }
          </div>
        }

        <!-- Reusable: one saved address the user can pick -->
        <ng-template #addressCard let-a="a" let-group="group">
          <label class="flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer transition-all"
            [ngClass]="a.isDefault ? 'border-amber-400 bg-amber-50/60 ring-1 ring-amber-200' : 'border-gray-200 bg-white hover:border-gray-300'">
            <input type="radio" class="mt-1 h-4 w-4 accent-amber-500" [name]="group" (click)="onAddressSelection(a)" [checked]="a.isDefault" />
            <div class="flex-1 min-w-0 text-sm text-gray-600 leading-snug">
              <div class="flex flex-wrap items-center gap-2">
                <span class="font-semibold text-gray-900">{{ a.firstName + " " + a.lastName }}</span>
                @if (a.isDefault) {
                  <span class="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-400 text-neutral-900">Default</span>
                }
              </div>
              <div class="mt-0.5">
                {{ a.address1 }}@if (a.address2) {
                <span>, {{ a.address2 }}</span>
                }, {{ a.city }}
              </div>
              <div class="mt-0.5 flex items-center gap-2">
                <span class="text-xs font-bold tracking-wide px-2 py-0.5 rounded-md bg-neutral-900 text-amber-400">{{ a.postCode }}</span>
                <span class="text-xs text-gray-400">{{ a.country }}</span>
              </div>
              @if (a.phoneNo) {
                <div class="mt-1 text-xs text-gray-500">{{ a.phoneNo }}</div>
              }
            </div>
            <button type="button" class="h-8 w-8 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-800 flex items-center justify-center" matTooltip="Edit address"
              (click)="$event.preventDefault(); onCreateNewAddress(a)">
              <mat-icon class="!text-[17px] !h-[17px] !w-[17px]">edit</mat-icon>
            </button>
          </label>
        </ng-template>

        @if (currentStep === 2) {
          <div class="flex flex-col gap-6">

            <!-- Who is buying -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button type="button" class="text-left rounded-2xl border-2 px-5 py-4 transition-all bg-white"
                [ngClass]="customerMode === 'CUSTOMER' ? 'border-amber-400 ring-4 ring-amber-100 shadow-sm' : 'border-gray-200 hover:border-gray-300'"
                (click)="onCustomerModeSelection('CUSTOMER')">
                <div class="flex items-center gap-3">
                  <span class="h-10 w-10 rounded-xl flex items-center justify-center" [ngClass]="customerMode === 'CUSTOMER' ? 'bg-amber-400 text-neutral-900' : 'bg-gray-100 text-gray-500'">
                    <mat-icon>person_search</mat-icon>
                  </span>
                  <div>
                    <div class="font-semibold text-gray-900">Existing customer</div>
                    <div class="text-xs text-gray-500">Pick from your customers, use their saved addresses</div>
                  </div>
                </div>
              </button>
              <button type="button" class="text-left rounded-2xl border-2 px-5 py-4 transition-all bg-white"
                [ngClass]="customerMode === 'GUEST' ? 'border-amber-400 ring-4 ring-amber-100 shadow-sm' : 'border-gray-200 hover:border-gray-300'"
                (click)="onCustomerModeSelection('GUEST')">
                <div class="flex items-center gap-3">
                  <span class="h-10 w-10 rounded-xl flex items-center justify-center" [ngClass]="customerMode === 'GUEST' ? 'bg-amber-400 text-neutral-900' : 'bg-gray-100 text-gray-500'">
                    <mat-icon>badge</mat-icon>
                  </span>
                  <div>
                    <div class="font-semibold text-gray-900">Guest checkout</div>
                    <div class="text-xs text-gray-500">Type the customer's details in, no account needed</div>
                  </div>
                </div>
              </button>
            </div>

            @if (customerMode === 'CUSTOMER') {
              <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div class="font-semibold text-gray-900">Find the customer</div>
                  <div class="text-sm text-gray-500">
                    Not found?
                    <button class="font-semibold text-emerald-700 hover:text-emerald-900 underline" (click)="onCreateCustomer()">Create new</button>
                  </div>
                </div>
                <app-customer-typehead
                  [searchFn]="searchCustomers"
                  [prevData]="prefillCustomer"
                  (onModelChangedEvent)="onCustomerSelection($event)"
                ></app-customer-typehead>

                @if (selectedCustomer) {
                  <!-- Selected customer -->
                  <div class="mt-5 rounded-xl border border-gray-100 bg-gray-50/60 p-4 flex items-center gap-4">
                    <div class="h-12 w-12 rounded-full bg-neutral-900 text-amber-400 font-bold flex items-center justify-center flex-shrink-0">
                      {{ (selectedCustomer.firstname || '?')[0] | uppercase }}{{ (selectedCustomer.lastname || '')[0] | uppercase }}
                    </div>
                    <div class="min-w-0">
                      <div class="font-bold text-gray-900 text-lg leading-tight">{{ selectedCustomer.firstname + ' ' + selectedCustomer.lastname }}</div>
                      <div class="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                        @if (selectedCustomer.useremail) {
                          <span class="flex items-center gap-1.5 break-all"><mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400">mail</mat-icon>{{ selectedCustomer.useremail }}</span>
                        }
                        @if (selectedCustomer.phone) {
                          <span class="flex items-center gap-1.5"><mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400">call</mat-icon>{{ selectedCustomer.phone }}</span>
                        }
                      </div>
                    </div>
                  </div>

                  <!-- What they have bought before: closed until the agent wants it -->
                  @if (recommendations?.customerHistory?.length) {
                    <details class="group mt-5 rounded-xl border border-amber-100 bg-amber-50/40">
                      <summary class="cursor-pointer list-none flex flex-wrap items-center gap-2 px-4 py-3 hover:bg-amber-50 rounded-xl transition-colors">
                        <mat-icon class="!text-amber-500 !text-[20px] !h-5 !w-5">history</mat-icon>
                        <span class="font-semibold text-gray-900 text-sm">{{ selectedCustomer.firstname }} has bought before</span>
                        <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{{ recommendations!.customerHistory.length }} product{{ recommendations!.customerHistory.length === 1 ? '' : 's' }}</span>
                        <span class="text-xs text-gray-500 hidden sm:inline">Click to see them</span>
                        <mat-icon class="ml-auto !text-[22px] !h-[22px] !w-[22px] text-gray-400 transition-transform group-open:rotate-180">expand_more</mat-icon>
                      </summary>
                      <div class="px-4 pb-4 pt-1">
                        <div class="text-xs text-gray-500 mb-3">Click one to look at it again for this sale</div>
                        <ng-container *ngTemplateOutlet="suggestionCards; context: { items: recommendations!.customerHistory }"></ng-container>
                      </div>
                    </details>
                  }

                  <!-- Addresses -->
                  <div class="mt-5">
                    <div class="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Billing address</div>
                    @if (billingAddresses.length > 0) {
                      <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        @for (address of billingAddresses; track $index) {
                          <ng-container *ngTemplateOutlet="addressCard; context: { a: address, group: 'addressSelectionBilling' }"></ng-container>
                        }
                      </div>
                    } @else {
                      <div class="rounded-xl border border-dashed border-gray-300 px-4 py-5 text-sm text-gray-500 text-center">No billing addresses found for this customer.</div>
                    }

                    <label class="mt-5 flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors">
                      <input type="checkbox" class="h-4 w-4 accent-amber-500" name="sameAddressCheckbox" [(ngModel)]="sameAddress" (change)="onSamaddressChange()" />
                      <span class="text-sm font-medium text-gray-800">Deliver to the billing address</span>
                    </label>

                    @if (!sameAddress) {
                      <div class="mt-5">
                        <div class="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Delivery address</div>
                        @if (shippingAddresses.length > 0) {
                          <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            @for (address of shippingAddresses; track $index) {
                              <ng-container *ngTemplateOutlet="addressCard; context: { a: address, group: 'addressSelectionShipping' }"></ng-container>
                            }
                          </div>
                        } @else {
                          <div class="rounded-xl border border-dashed border-gray-300 px-4 py-5 text-sm text-gray-500 text-center">No delivery addresses found for this customer.</div>
                        }
                      </div>
                    }

                    <button class="mt-5 flex items-center gap-1.5 text-sm font-semibold text-amber-700 hover:text-amber-900" (click)="onCreateNewAddress()">
                      <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">add_location_alt</mat-icon> Add a new billing or delivery address
                    </button>
                  </div>
                }
              </div>
            }

            @if (customerMode === 'GUEST') {
              <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div class="font-semibold text-gray-900 mb-3">Customer details</div>
                <app-billing-form [defaultBillingAddress]="defaultBillingAddress" [defaultShippingAddress]="defaultShippingAddress" (formValid)="onBillingFormSubmit($event)"></app-billing-form>
              </div>
            }

            <!-- VAT number -->
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <form #vatNumForm="ngForm">
                <div class="flex items-center justify-between gap-2 mb-2">
                  <label class="font-semibold text-gray-900">VAT number</label>
                  <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Optional</span>
                </div>
                <input
                  class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm disabled:opacity-60"
                  name="del-company-name"
                  type="text"
                  placeholder="e.g. GB 123 4567 89"
                  pattern="^GB(\d{3} \d{4} \d{2}|\d{9}|\d{9} \d{3}|GD([0-4][0-9]{2})|HA([5-9][0-9]{2}))$"
                  (ngModelChange)="onVatNumberChanged()"
                  [(ngModel)]="userVatNu"
                  #vatInput="ngModel"
                  [disabled]="userVatNu !== '' && userVatNu !== null && selectedCustomer"
                  />
                @if (vatInput.invalid && (vatInput.dirty || vatInput.touched)) {
                  <div class="mt-1 text-xs text-red-500">
                    @if (vatInput.errors?.['pattern']) {
                      <span>That doesn't look like a valid UK VAT number.</span>
                    }
                  </div>
                }
              </form>
            </div>
          </div>
        }
        @if (currentStep === 3) {
          <div class="flex flex-col gap-6">

            <!-- Delivery method -->
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div class="flex items-center gap-2 mb-4">
                <span class="h-9 w-9 rounded-xl bg-amber-50 flex items-center justify-center"><mat-icon class="!text-amber-500 !text-[20px] !h-5 !w-5">local_shipping</mat-icon></span>
                <div class="font-semibold text-gray-900">Delivery method</div>
              </div>
              <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
                @for (m of availableDeliveryMethods; track m.value) {
                  <button type="button" class="text-left rounded-xl border-2 px-4 py-3 transition-all"
                    [ngClass]="selectedDeliveryMethod == m.value ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-100' : 'border-gray-200 hover:border-gray-300'"
                    (click)="onDeleveryMethodSelection(m.value)">
                    <mat-icon class="!text-[22px] !h-[22px] !w-[22px]" [ngClass]="selectedDeliveryMethod == m.value ? '!text-amber-600' : '!text-gray-400'">{{ m.icon }}</mat-icon>
                    <div class="mt-1.5 font-semibold text-gray-900 text-sm">{{ m.label }}</div>
                    <div class="text-[11px] text-gray-500 leading-snug">{{ m.hint }}</div>
                  </button>
                }
              </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

              <!-- Coupons -->
              <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div class="flex items-center gap-2 mb-4">
                  <span class="h-9 w-9 rounded-xl bg-amber-50 flex items-center justify-center"><mat-icon class="!text-amber-500 !text-[20px] !h-5 !w-5">sell</mat-icon></span>
                  <div class="font-semibold text-gray-900">Discount coupon</div>
                </div>
                @if (hasCouponAdvice) {
                  <div class="mb-4">
                    <ng-container *ngTemplateOutlet="couponGuide; context: { cols: 1 }"></ng-container>
                  </div>
                }
                <div class="flex gap-2">
                  <input [(ngModel)]="enteredCouponCode" name="coupon-code" type="text" placeholder="Enter a coupon code"
                    class="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm" (keyup.enter)="onApplyCouponCode()" />
                  <button class="px-5 rounded-xl bg-neutral-900 text-amber-400 text-sm font-semibold hover:bg-neutral-800 transition-colors" (click)="onApplyCouponCode()">Apply</button>
                </div>
                @if (couponError) {
                  <div class="text-xs font-medium text-red-500 mt-2">{{ couponError }}</div>
                }
                @if (appliedCouponCodes.length > 0) {
                  <div class="mt-3 flex flex-wrap gap-2">
                    @for (ccode of appliedCouponCodes; track $index) {
                      <span class="inline-flex items-center gap-1 text-xs font-semibold pl-3 pr-1.5 py-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100">
                        {{ ccode.couponCode }}: {{ ccode.couponValue }} {{ccode.couponPerc ? '%' : '£'}} off
                        <button class="h-5 w-5 rounded-full hover:bg-emerald-200 flex items-center justify-center" (click)="onRemoveCouponCode(ccode, $index)" aria-label="Remove coupon">
                          <mat-icon class="!text-[13px] !h-[13px] !w-[13px]">close</mat-icon>
                        </button>
                      </span>
                    }
                  </div>
                }
              </div>

              <!-- Loyalty points -->
              <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div class="flex items-center gap-2 mb-4">
                  <span class="h-9 w-9 rounded-xl bg-amber-50 flex items-center justify-center"><mat-icon class="!text-amber-500 !text-[20px] !h-5 !w-5">stars</mat-icon></span>
                  <div class="font-semibold text-gray-900">Loyalty points</div>
                </div>
                <div class="flex items-end gap-3">
                  <div class="rounded-xl bg-gray-50 border border-gray-100 px-4 py-2.5">
                    <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Available</div>
                    <div class="text-xl font-extrabold text-gray-900">{{ customerInfo && customerMode == 'CUSTOMER' ? customerInfo?.userpoints : 0 }}</div>
                  </div>
                  <div class="flex-1">
                    <label class="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1" for="loyality-redeem-amount">Points to redeem</label>
                    <input [(ngModel)]="loyaltyRedeemAmount" min="0" max="{{ loyaltyPoint }}" name="loyalty-redeem-amount" type="number"
                      class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm" />
                  </div>
                  <button class="px-5 py-2.5 rounded-xl bg-neutral-900 text-amber-400 text-sm font-semibold hover:bg-neutral-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  (click)="onLoyaltyRedemption()" [disabled]="localCart.items?.length == 0 || loyaltyPoint == 0">Redeem</button>
                </div>
                @if (loyaltyRedemptionError) {
                  <div class="mt-2 text-xs text-red-500">{{ loyaltyRedemptionError }}</div>
                }
                @if (loyaltyRedeemed) {
                  <div class="mt-3">
                    <span class="inline-flex items-center gap-1 text-xs font-semibold pl-3 pr-1.5 py-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100">
                      <strong>{{ loyaltyRedeemed }}</strong> points applied
                      <button class="h-5 w-5 rounded-full hover:bg-emerald-200 flex items-center justify-center" (click)="onLoyaltyPointsRemoval()" aria-label="Remove points">
                        <mat-icon class="!text-[13px] !h-[13px] !w-[13px]">close</mat-icon>
                      </button>
                    </span>
                  </div>
                }
                <details class="mt-4 text-xs text-gray-500">
                  <summary class="cursor-pointer font-semibold text-gray-600">Loyalty point rules</summary>
                  <ol class="mt-2 list-decimal pl-5 space-y-0.5">
                    <li>1 point is equivalent to £1.00</li>
                    <li>Minimum usage is 50 points on a single cart.</li>
                    <li>Maximum usage is 500 points on a single cart.</li>
                    <li>The cart total must be over £{{ customerInfo?.minOrderValue }} to use loyalty points.</li>
                  </ol>
                </details>
              </div>
            </div>

            <!-- Payment method -->
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div class="flex items-center gap-2 mb-4">
                <span class="h-9 w-9 rounded-xl bg-amber-50 flex items-center justify-center"><mat-icon class="!text-amber-500 !text-[20px] !h-5 !w-5">payments</mat-icon></span>
                <div class="font-semibold text-gray-900">Payment method</div>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                @if (netTotal) {
                  @if (paymentOn('TEYA')) {
                    <button type="button" class="flex items-center justify-between gap-3 rounded-xl border-2 px-4 py-3.5 text-left transition-all"
                      [ngClass]="selectedPaymentOption == 'TEYA' ? 'border-emerald-500 bg-emerald-50/60 ring-2 ring-emerald-100' : 'border-gray-200 hover:border-gray-300'"
                      (click)="onPaymentMethodSelection('TEYA')">
                      <span class="flex items-center gap-3">
                        <mat-icon [ngClass]="selectedPaymentOption == 'TEYA' ? '!text-emerald-600' : '!text-gray-300'">{{ selectedPaymentOption == 'TEYA' ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
                        <span class="font-semibold text-sm text-gray-900">Debit / credit card</span>
                      </span>
                      <img src="https://nustone-bucket.s3.eu-west-2.amazonaws.com/assets/teya.avif" alt="Teya" class="max-h-7 max-w-[56px] object-contain" />
                    </button>
                  }
                  @if (paymentOn('PAYPAL')) {
                    <button type="button" class="flex items-center justify-between gap-3 rounded-xl border-2 px-4 py-3.5 text-left transition-all"
                      [ngClass]="selectedPaymentOption == 'PAYPAL' ? 'border-emerald-500 bg-emerald-50/60 ring-2 ring-emerald-100' : 'border-gray-200 hover:border-gray-300'"
                      (click)="onPaymentMethodSelection('PAYPAL')">
                      <span class="flex items-center gap-3">
                        <mat-icon [ngClass]="selectedPaymentOption == 'PAYPAL' ? '!text-emerald-600' : '!text-gray-300'">{{ selectedPaymentOption == 'PAYPAL' ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
                        <span class="font-semibold text-sm text-gray-900">PayPal</span>
                      </span>
                      <img src="https://nustone-bucket.s3.eu-west-2.amazonaws.com/assets/paypal.svg" alt="PayPal" class="max-h-5 max-w-[56px] object-contain" />
                    </button>
                  }
                }
                @if (selectedDeliveryMethod !== 'EXPRESS' && selectedDeliveryMethod !== 'NEXTDAY') {
                  @if (paymentOn('BANK_TRANSFER')) {
                    <button type="button" class="flex items-center justify-between gap-3 rounded-xl border-2 px-4 py-3.5 text-left transition-all"
                      [ngClass]="selectedPaymentOption == 'BANK_TRANSFER' ? 'border-emerald-500 bg-emerald-50/60 ring-2 ring-emerald-100' : 'border-gray-200 hover:border-gray-300'"
                      (click)="onPaymentMethodSelection('BANK_TRANSFER')">
                      <span class="flex items-center gap-3">
                        <mat-icon [ngClass]="selectedPaymentOption == 'BANK_TRANSFER' ? '!text-emerald-600' : '!text-gray-300'">{{ selectedPaymentOption == 'BANK_TRANSFER' ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
                        <span class="font-semibold text-sm text-gray-900">Bank transfer</span>
                      </span>
                      <img src="https://nustone-bucket.s3.eu-west-2.amazonaws.com/assets/bank-transfer.webp" alt="Bank transfer" class="max-h-5 max-w-[56px] object-contain" />
                    </button>
                  }
                }
                @if (paymentOn('INVOICE')) {
                  <button type="button" class="flex items-center justify-between gap-3 rounded-xl border-2 px-4 py-3.5 text-left transition-all"
                    [ngClass]="selectedPaymentOption == 'INVOICE' ? 'border-emerald-500 bg-emerald-50/60 ring-2 ring-emerald-100' : 'border-gray-200 hover:border-gray-300'"
                    (click)="onPaymentMethodSelection('INVOICE')">
                    <span class="flex items-center gap-3">
                      <mat-icon [ngClass]="selectedPaymentOption == 'INVOICE' ? '!text-emerald-600' : '!text-gray-300'">{{ selectedPaymentOption == 'INVOICE' ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
                      <span class="font-semibold text-sm text-gray-900">Invoice</span>
                    </span>
                    <mat-icon class="!text-gray-300">receipt_long</mat-icon>
                  </button>
                }
              </div>
            </div>
          </div>
        }

      </div>
      <!-- Content area -->

      <!-- Basket + order summary -->
      @if (localCart.items?.length > 0) {
        <div class="xl:col-span-2 flex flex-col gap-4 xl:sticky xl:top-20">

          <app-cart [localCart]="localCart" (onItemRmovedEvent)="onCartItemRemoval($event)"></app-cart>

          @if (subTotal && subTotal > 0) {
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div class="flex items-center gap-2 mb-3">
                <mat-icon class="!text-amber-500 !text-[20px] !h-5 !w-5">receipt_long</mat-icon>
                <h2 class="font-semibold text-gray-900">Order summary</h2>
              </div>

              @if (priceOnRequest) {
                <div class="flex items-start gap-2 rounded-xl bg-red-50 border border-red-100 p-3 text-sm font-semibold text-red-600">
                  <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">info</mat-icon>
                  <span>Shipping to <strong>{{ postCode }}</strong> is price on request. Please contact customer support.</span>
                </div>
              } @else {
                @if (shipCost >= 0) {
                  <div class="flex justify-between text-sm py-1">
                    <span class="text-gray-500">Delivery</span>
                    <span class="font-medium text-gray-800">{{ deliveryType }}</span>
                  </div>
                }
                @if (shipCost > 0) {
                  <div class="flex justify-between text-sm py-1">
                    <span class="text-gray-500">Shipping cost</span>
                    <span class="font-semibold text-gray-900">{{ "£" + shipCost.toFixed(2) }}</span>
                  </div>
                }
              }

              @if (handleCharge > 0) {
                <div class="flex justify-between text-sm py-1">
                  <span class="text-gray-500">Handling fee</span>
                  <span class="font-semibold text-gray-900">{{ "£" + handleCharge.toFixed(2) }}</span>
                </div>
              }
              @for (discount of redeemedDicounts; track discount) {
                <div class="flex justify-between text-sm py-1 text-emerald-700">
                  <span>{{ discount.title }}</span>
                  <span class="font-semibold">- {{ "£" + discount.discountedAmount.toFixed(2) }}</span>
                </div>
              }
              @if (loyaltyRedeemed) {
                <div class="flex justify-between text-sm py-1 text-emerald-700">
                  <span>{{ loyaltyRedeemed }} loyalty points redeemed</span>
                  <span class="font-semibold">- {{ "£" + loyaltyRedeemed.toFixed(2) }}</span>
                </div>
              }

              <div class="mt-3 pt-3 border-t border-gray-100 flex justify-between items-baseline">
                <span class="font-semibold text-gray-800">Net amount</span>
                <div class="text-right">
                  <div class="text-3xl font-extrabold text-gray-900 leading-none">{{ "£" + netTotal.toFixed(2) }}</div>
                  @if (netTotal > 0) {
                    <div class="text-xs text-gray-400 mt-1">{{ "includes £" + vatAmount.toFixed(2) + " VAT" }}</div>
                  }
                </div>
              </div>

              <div class="mt-5">
                <label class="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Order notes (optional)</label>
                <textarea [(ngModel)]="orderNotes" rows="4"
                  placeholder="Anything the delivery team should know: access restrictions, height or width limits, a gate code, or a request to delay dispatch."
                class="mt-1 w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm resize-y placeholder:text-gray-300"></textarea>
              </div>

              @if (currentStep == 3) {
                <div class="mt-5">
                  <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1">Terms and conditions</div>
                  <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 h-44 overflow-y-scroll text-sm">
                    <app-tnc></app-tnc>
                  </div>
                  <label class="mt-3 flex items-start gap-2.5 rounded-xl px-3 py-2.5 cursor-pointer transition-colors"
                    [ngClass]="tncAccepted ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'">
                    <input type="checkbox" class="mt-0.5 h-4 w-4 accent-emerald-600" [(ngModel)]="tncAccepted" />
                    <span class="text-xs text-gray-700 leading-relaxed">
                      I have read and agree to the website
                      <a class="font-semibold underline" href="/terms-and-conditions" target="_blank">terms and conditions</a>,
                      <a class="font-semibold underline" href="https://nustone.co.uk/delivery/" target="_blank">delivery information</a> and
                      <a class="font-semibold underline" href="https://nustone.co.uk/waste-and-damages/" target="_blank">damages policy</a>. <span class="text-red-500">*</span>
                    </span>
                  </label>
                </div>
              }
            </div>
          }
        </div>
      }
      <!-- Basket + order summary -->

    </div>
  </div>

  <!-- Bottom bar -->
  @if (localCart.items.length > 0) {
    <div
      class="sticky bottom-0 z-30 bg-white/95 backdrop-blur border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] px-6 py-3 flex flex-row items-center gap-4"
      >
      <div class="hidden sm:flex items-center gap-3 mr-auto">
        <div class="h-10 w-10 rounded-xl bg-neutral-900 text-amber-400 flex items-center justify-center">
          <mat-icon>shopping_basket</mat-icon>
        </div>
        <div>
          <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{{ localCart.items.length }} item{{ localCart.items.length === 1 ? '' : 's' }} in basket</div>
          <div class="text-lg font-extrabold text-gray-900 leading-tight">£{{ (netTotal || localCart.total || 0) | number:'1.2-2' }}</div>
        </div>
      </div>
      @if (paypalPaymentError !== '') {
        <div class="text-xs text-red-500 text-right max-w-xs">{{ paypalPaymentError }}</div>
      }
      <button class="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors" (click)="onResetAll()">Reset</button>
      @if (currentStep !== 1) {
        <button class="flex items-center gap-1 px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors" (click)="onBack()">
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">arrow_back</mat-icon> Back
        </button>
      }
      @if (currentStep == 3 && selectedPaymentOption == 'PAYPAL' && !paypalPaymentComplete && tncAccepted && !orderSubmitted) {
        <button class="px-6 py-3 rounded-xl bg-gray-200 text-gray-500 text-sm font-semibold cursor-not-allowed" disabled>Complete payment above</button>
      } @else {
        <button
          class="flex items-center gap-2 px-7 py-3 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-bold text-sm shadow-sm hover:shadow-md hover:from-amber-300 hover:to-amber-400 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-sm"
          (click)="onConfirmAndNext()"
          [disabled]="(currentStep == 3 && !tncAccepted) || orderSubmitted"
          >
          {{ currentStep == 3 ? 'Submit order' : 'Confirm & next' }}
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">{{ currentStep == 3 ? 'check' : 'arrow_forward' }}</mat-icon>
        </button>
      }
    </div>
  }

  <!-- Paypal / Debit-Credit-Card widget - centered overlay so PayPal's expanded card form has room -->
  @if (currentStep == 3 && selectedPaymentOption == 'PAYPAL' && !paypalPaymentComplete && tncAccepted && !orderSubmitted) {
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" (click)="onPaymentMethodSelection('INVOICE')">
      <div class="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto p-6" (click)="$event.stopPropagation()">
        <button
          type="button"
          class="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-xl leading-none"
          (click)="onPaymentMethodSelection('INVOICE')"
          aria-label="Close"
        >&times;</button>
        <app-paypal-button
          [totalAmount]="netTotal"
          [payerInfo]="defaultBillingAddress"
          (paypalWalletClicked)="onPaypalWalletClicked()"
          (paypalResponseRecieved)="onPaypalResponseRecieved($event)"
        ></app-paypal-button>
      </div>
    </div>
  }

  <!-- Navigation buttons -->
  <!-- test -->